import type { ConsolidatedProfileData } from '@/types';
import { CATEGORY_QUESTIONS } from '@/data/category-questions';
import { HIS_SIZE_SCALE, HIS_GIRTH_SCALE, HIS_CUM_SCALE, HIS_CONSISTENCY_SCALE, HIS_TASTE_SCALE, HIS_PRECUM_SCALE, HER_WETNESS_SCALE, HER_SQUIRT_SCALE, HER_SENSITIVITY_SCALE, HER_ORGASM_EASE_SCALE } from '@/data/profile-scales';

// Resolve [WATCH_CLIP:clipId:title] markers in text
function resolveClipMarkers(text: string, clips: ConsolidatedProfileData['clips']): string {
  return text.replace(/\[WATCH_CLIP:([^:]+):([^\]]+)\]/g, (_match, clipId, title) => {
    const clip = clips.find(c => c.id === clipId);
    return clip ? `(Clip: ${clip.title})` : `(Clip: ${title || 'unknown'})`;
  });
}

// Get scale label for a numeric value
function scaleLabel(scales: readonly { value: number; label: string }[], value: number | undefined): string | null {
  if (value === undefined) return null;
  const entry = scales.find(s => s.value === value);
  return entry ? `${entry.label} (${value})` : `${value}`;
}

// Format a date string to a short readable format
function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return iso;
  }
}

// Get threaded replies for a follow-up question
function getReplies<T extends { id: string; parentId?: string }>(items: T[], parentId: string): T[] {
  return items.filter(item => item.parentId === parentId);
}

// Build the reviewer background section
function buildReviewerBackground(data: ConsolidatedProfileData): string[] {
  const r = data.reviewer;
  const lines: string[] = [];

  const fields: [string, string | undefined][] = [
    ['Gender', r.gender],
    ['Age Range', r.ageRange],
    ['Relationship', r.relationshipStatus],
    ['Sexually Active', r.sexuallyActive],
    ['Watches Adult Content', r.watchesAdultContent],
    ['Content Themes', r.adultContentThemes],
    ['Content Example', r.adultContentExample],
    ['What Makes Good Content', r.whatMakesGoodContent],
    ['What Feels Fake', r.whatFeelsFake],
    ['Preferred Style', r.preferredStyle],
    ['Familiar With Products', r.familiarWithProducts],
    ['Relevant Experience', r.relevantExperience],
    ['Why Interested', r.whyInterested],
    ['Enjoys Volume Content', r.enjoysVolumeContent],
    ['Tried Load Boost', r.triedLoadBoost],
    ['Load Boost Experience', r.loadBoostExperience],
    ['Partner Typical Volume', r.partnerTypicalVolume],
    ['Partner Volume Like/Dislike', r.partnerVolumeLikeDislike],
    ['Want Partner Load Boost', r.wantPartnerLoadBoost],
    ['Enjoys Swallowing', r.enjoysSwallowing],
    ['Swallowing Description', r.swallowingDescription],
    ['Would Enjoy Better Taste', r.wouldEnjoyBetterTaste],
    ['Enjoys Facials', r.enjoysFacials],
    ['Volume Makes Difference', r.volumeMakesDifference],
    ['Can Squirt', r.canSquirt],
    ['Squirt Scenario', r.squirtScenarioDescription],
    ['Want To Squirt More', r.wantToSquirtMore],
    ['Used Arousal Products', r.usedArousalProducts],
    ['Orgasm From Sex', r.orgasmFromSex],
    ['Typical Wetness', r.typicalWetness !== undefined ? `${r.typicalWetness}/5` : undefined],
    ['Wetness Notes', r.wetnessNotes],
    ['Want To Be Wetter', r.wantToBeWetter],
    ['Enjoys Squirting Content', r.enjoysSquirtingContent],
    ['Want More With Partner', r.wantMoreWithPartner],
    ['Partner Used Arousal Products', r.partnerUsedArousalProducts],
    ['Last Sexual Activity', r.lastSexualActivity],
    ['Facial Experience', r.facialExperience],
    ['Oral Experience', r.oralExperience],
    ['Anal Experience', r.analExperience],
    ['Favorite Sexual Memory', r.favoriteSexualMemory],
    ['Fantasies Not Tried', r.fantasiesNotTried],
    ['Biggest Turn Ons', r.biggestTurnOns],
    ['Deal Breakers', r.dealBreakers],
    ['Additional Notes', r.additionalNotes],
  ];

  for (const [label, value] of fields) {
    if (value && value.trim()) {
      lines.push(`${label}: ${value}`);
    }
  }

  return lines;
}

// Build reviewer custom Q&A section
function buildReviewerCustomQA(data: ConsolidatedProfileData): string[] {
  const lines: string[] = [];
  const items = data.reviewerFollowUps
    .filter(fu => fu.status !== 'dismissed' && !fu.parentId)
    .sort((a, b) => new Date(b.sentAt).getTime() - new Date(a.sentAt).getTime());

  if (items.length === 0) return [];

  const answeredCount = items.filter(i => i.status === 'answered').length;
  lines.push(`--- REVIEWER CUSTOM Q&A (${answeredCount} answered) ---`);

  for (const q of items) {
    const questionText = resolveClipMarkers(q.question, data.clips);
    lines.push(`Q: ${questionText}`);
    if (q.context) lines.push(`   Context: ${q.context}`);

    if (q.answer) {
      lines.push(`A: "${resolveClipMarkers(q.answer, data.clips)}"`);
      if (q.answeredAt) lines.push(`(Answered: ${formatDate(q.answeredAt)})`);
    } else if (q.status === 'queued') {
      lines.push(`[Queued - not yet sent]`);
    } else {
      lines.push(`[Pending response]`);
    }

    // Render threaded replies
    const replies = getReplies(data.reviewerFollowUps, q.id);
    for (const reply of replies) {
      lines.push('');
      lines.push(`  > Follow-up: ${resolveClipMarkers(reply.question, data.clips)}`);
      if (reply.answer) {
        lines.push(`  > A: "${resolveClipMarkers(reply.answer, data.clips)}"`);
        if (reply.answeredAt) lines.push(`  (Answered: ${formatDate(reply.answeredAt)})`);
      } else {
        lines.push(`  > [Pending response]`);
      }
    }

    lines.push('');
  }

  return lines;
}

// Build couple profile stats section
function buildCoupleProfile(data: ConsolidatedProfileData): string[] {
  if (!data.coupleData) return [];
  const couple = data.coupleData;
  const lines: string[] = [];

  const his = couple.hisProfile;
  const her = couple.herProfile;
  if (!his && !her) return [];

  lines.push(`--- COUPLE PROFILE ---`);

  if (his) {
    const hisFields: [string, string | null][] = [
      ['His Size', scaleLabel(HIS_SIZE_SCALE as unknown as { value: number; label: string }[], his.size)],
      ['His Girth', scaleLabel(HIS_GIRTH_SCALE as unknown as { value: number; label: string }[], his.girth)],
      ['His Cum Intensity', scaleLabel(HIS_CUM_SCALE as unknown as { value: number; label: string }[], his.cumIntensity)],
      ['His Consistency', scaleLabel(HIS_CONSISTENCY_SCALE as unknown as { value: number; label: string }[], his.cumConsistency)],
      ['Consistency Notes', his.consistencyNotes || null],
      ['His Taste', scaleLabel(HIS_TASTE_SCALE as unknown as { value: number; label: string }[], his.cumTaste)],
      ['Taste Notes', his.tasteNotes || null],
      ['His Precum', scaleLabel(HIS_PRECUM_SCALE as unknown as { value: number; label: string }[], his.precumProduction)],
      ['Precum Notes', his.precumNotes || null],
      ['Visual Sharing', his.visualSharingOpen || null],
    ];
    for (const [label, value] of hisFields) {
      if (value) lines.push(`${label}: ${value}`);
    }
  }

  if (her) {
    if (his) lines.push(''); // Spacer between his and her
    const herFields: [string, string | null][] = [
      ['Her Wetness', scaleLabel(HER_WETNESS_SCALE as unknown as { value: number; label: string }[], her.typicalWetness)],
      ['Wetness Notes', her.wetnessNotes || null],
      ['Her Squirting', scaleLabel(HER_SQUIRT_SCALE as unknown as { value: number; label: string }[], her.squirtingAbility)],
      ['Squirting Notes', her.squirtingNotes || null],
      ['Her Sensitivity', scaleLabel(HER_SENSITIVITY_SCALE as unknown as { value: number; label: string }[], her.sensitivity)],
      ['Sensitivity Notes', her.sensitivityNotes || null],
      ['Her Orgasm Ease', scaleLabel(HER_ORGASM_EASE_SCALE as unknown as { value: number; label: string }[], her.orgasmEase)],
      ['Orgasm Notes', her.orgasmNotes || null],
      ['Visual Sharing', her.visualSharingOpen || null],
    ];
    for (const [label, value] of herFields) {
      if (value) lines.push(`${label}: ${value}`);
    }
  }

  lines.push('');
  return lines;
}

// Build couple category Q&A section
function buildCoupleCategoryQA(data: ConsolidatedProfileData): string[] {
  if (!data.coupleData?.categoryQA) return [];

  const lines: string[] = [];
  const categoryQA = data.coupleData.categoryQA;
  const entries = Object.entries(categoryQA);
  if (entries.length === 0) return [];

  lines.push(`--- COUPLE CATEGORY Q&A ---`);

  for (const [_categoryId, response] of entries) {
    const answerEntries = Object.entries(response.answers);
    for (const [questionId, answer] of answerEntries) {
      if (!answer || !answer.trim()) continue;
      const questionDef = CATEGORY_QUESTIONS.find(q => q.id === questionId);
      const questionText = questionDef ? questionDef.question : 'Unknown question';
      const categoryLabel = questionDef?.categoryId || response.categoryId || '';
      lines.push(`[${categoryLabel}] ${questionText}`);
      lines.push(`A: "${answer}"`);
      lines.push('');
    }
  }

  return lines;
}

// Build couple AI follow-ups section
function buildCoupleAIFollowUps(data: ConsolidatedProfileData): string[] {
  const items = data.coupleFollowUps
    .filter(fu => fu.status !== 'dismissed' && !fu.parentId)
    .sort((a, b) => new Date(b.sentAt).getTime() - new Date(a.sentAt).getTime());

  if (items.length === 0) return [];

  const lines: string[] = [];
  const answeredCount = items.filter(i => i.status === 'answered').length;
  lines.push(`--- COUPLE AI FOLLOW-UPS (${answeredCount} answered) ---`);

  for (const q of items) {
    const questionText = resolveClipMarkers(q.question, data.clips);
    lines.push(`Q: ${questionText}`);
    if (q.context) lines.push(`   Context: ${q.context}`);

    if (q.answer) {
      lines.push(`A: "${resolveClipMarkers(q.answer, data.clips)}"`);
      const meta: string[] = [];
      if (q.answeredBy) meta.push(`Answered by: ${q.answeredBy}`);
      if (q.answeredAt) meta.push(formatDate(q.answeredAt));
      if (meta.length) lines.push(`(${meta.join(', ')})`);

      if (q.reviewerResponse) {
        lines.push(`  Reviewer response: "${q.reviewerResponse}"`);
        if (q.reviewerResponderName) lines.push(`  (by ${q.reviewerResponderName})`);
      }
    } else if (q.status === 'queued') {
      lines.push(`[Queued - not yet sent]`);
    } else {
      lines.push(`[Pending response]`);
    }

    // Replies
    const replies = getReplies(data.coupleFollowUps, q.id);
    for (const reply of replies) {
      lines.push('');
      lines.push(`  > Follow-up: ${resolveClipMarkers(reply.question, data.clips)}`);
      if (reply.answer) {
        lines.push(`  > A: "${resolveClipMarkers(reply.answer, data.clips)}"`);
        const meta: string[] = [];
        if (reply.answeredBy) meta.push(`by ${reply.answeredBy}`);
        if (reply.answeredAt) meta.push(formatDate(reply.answeredAt));
        if (meta.length) lines.push(`  (${meta.join(', ')})`);
      }
    }

    lines.push('');
  }

  return lines;
}

// Build couple questions from reviewer section
function buildCoupleQuestions(data: ConsolidatedProfileData): string[] {
  const items = data.coupleQuestions
    .filter(q => q.fromReviewerId === data.reviewer.id && !q.parentId && q.status !== 'dismissed')
    .sort((a, b) => new Date(b.sentAt).getTime() - new Date(a.sentAt).getTime());

  if (items.length === 0) return [];

  const lines: string[] = [];
  const answeredCount = items.filter(i => i.status === 'answered').length;
  lines.push(`--- COUPLE QUESTIONS FROM REVIEWER (${answeredCount} answered) ---`);

  for (const q of items) {
    const questionText = resolveClipMarkers(q.question, data.clips);
    lines.push(`Q: ${questionText}`);
    if (q.context) lines.push(`   Context: ${q.context}`);
    if (q.category) lines.push(`   Category: ${q.category}`);

    if (q.answer) {
      lines.push(`A: "${resolveClipMarkers(q.answer, data.clips)}"`);
      const meta: string[] = [];
      if (q.answeredBy) meta.push(`Answered by: ${q.answeredBy}`);
      if (q.answeredAt) meta.push(formatDate(q.answeredAt));
      if (meta.length) lines.push(`(${meta.join(', ')})`);
    } else {
      lines.push(`[Pending response]`);
    }

    // Replies
    const replies = getReplies(data.coupleQuestions, q.id);
    for (const reply of replies) {
      lines.push('');
      lines.push(`  > Follow-up: ${resolveClipMarkers(reply.question, data.clips)}`);
      if (reply.answer) {
        lines.push(`  > A: "${resolveClipMarkers(reply.answer, data.clips)}"`);
        const meta: string[] = [];
        if (reply.answeredBy) meta.push(`by ${reply.answeredBy}`);
        if (reply.answeredAt) meta.push(formatDate(reply.answeredAt));
        if (meta.length) lines.push(`  (${meta.join(', ')})`);
      }
    }

    lines.push('');
  }

  return lines;
}

// Build clip review feedback section
function buildClipFeedback(data: ConsolidatedProfileData): string[] {
  const suggestions = data.clipSuggestions.filter(cs =>
    cs.reviewerId === data.reviewer.id || cs.reviewerId === data.reviewer.code
  );

  if (suggestions.length === 0) return [];

  const lines: string[] = [];
  lines.push(`--- CLIP REVIEW FEEDBACK ---`);

  for (const cs of suggestions) {
    for (const clip of cs.clips) {
      if (clip.rating) {
        lines.push(`"${clip.title}" - ${clip.rating}/5 stars${clip.interested !== undefined ? (clip.interested ? ' - Would try' : ' - Not interested') : ''}`);
        if (clip.comment) lines.push(`  Comment: "${clip.comment}"`);
      } else {
        lines.push(`"${clip.title}" - NOT YET REVIEWED`);
      }
    }
    lines.push('');
  }

  return lines;
}

// Main export function
export function buildProfileText(data: ConsolidatedProfileData): string {
  const sections: string[][] = [];

  // Header
  const header: string[] = [];
  header.push(`PROFILE: ${data.reviewer.name || data.reviewer.id} (${data.reviewer.code})`);
  if (data.coupleData) {
    header.push(`Couple: ${data.coupleData.hisName} & ${data.coupleData.herName} (${data.coupleData.coupleId})`);
  }
  header.push(`Generated: ${formatDate(new Date().toISOString())}`);
  header.push('');
  sections.push(header);

  // Section 1: Reviewer background
  const bg = buildReviewerBackground(data);
  if (bg.length > 0) {
    sections.push(['--- REVIEWER BACKGROUND ---', ...bg, '']);
  }

  // Section 2: Reviewer custom Q&A
  const customQA = buildReviewerCustomQA(data);
  if (customQA.length > 0) sections.push(customQA);

  // Section 3: Couple profile stats
  const coupleProfile = buildCoupleProfile(data);
  if (coupleProfile.length > 0) sections.push(coupleProfile);

  // Section 4: Couple category Q&A
  const categoryQA = buildCoupleCategoryQA(data);
  if (categoryQA.length > 0) sections.push(categoryQA);

  // Section 5: Couple AI follow-ups
  const aiFollowUps = buildCoupleAIFollowUps(data);
  if (aiFollowUps.length > 0) sections.push(aiFollowUps);

  // Section 6: Couple questions from reviewer
  const coupleQs = buildCoupleQuestions(data);
  if (coupleQs.length > 0) sections.push(coupleQs);

  // Section 7: Clip review feedback
  const clipFeedback = buildClipFeedback(data);
  if (clipFeedback.length > 0) sections.push(clipFeedback);

  return sections.map(s => s.join('\n')).join('\n');
}
